/** biome-ignore-all lint/suspicious/noExplicitAny: old code */
import {
  type Cell,
  flexRender,
  type Row as TanStackRow,
  type RowData as TanStackRowData,
} from "@tanstack/react-table"
import clsx from "clsx"
import { createContext, useContext, useRef } from "react"
import { TableBody, TableCell, TableRow } from "../ui/table"
import { useDataTableCtx } from "./DataTable"
import { DataTablePinnedShadow } from "./DataTablePinnedShadow"
import { DataTableRowExpanded } from "./DataTableRowExpanded"
import { LoadingBar } from "./LoadingBar"
import type { DataTableScrollState } from "./types"

export function DataTableBody<TRowData extends TanStackRowData>({
  scrollState,
}: {
  scrollState: DataTableScrollState
}) {
  const { table, columns, elements, config } = useDataTableCtx<TRowData>()

  // If there are bodySpinner components, render them instead of the table content
  if (elements.bodySpinner) {
    return <>{elements.bodySpinner}</>
  }

  return (
    <TableBody>
      {config.shouldShowProgressIndicator && (
        <tr className="relative">
          <LoadingBar />
        </tr>
      )}

      {table.getRowModel().rows?.length ? (
        table
          .getRowModel()
          .rows.map((row) => <SingleRow key={row.id} row={row} scrollState={scrollState} />)
      ) : (
        <TableRow>
          <TableCell colSpan={columns.length} className="h-24 text-center">
            {/* TODO: add proper empty state here */}
            No results.
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  )
}

const DataTableRowCtx = createContext<TanStackRow<any> | null>(null)
/**
 * Must be used with generic type based on your usage
 */
export const useDataTableRow = <TRowData extends TanStackRowData>() => {
  const row = useContext(DataTableRowCtx)
  if (!row) {
    throw new Error("useDataTableRowCtx must be used within a DataTableRowCtx")
  }
  return row as TanStackRow<TRowData>
}

/**
 * Maybe in the future we allow totally custom row renderer as well
 */
function SingleRow<TRowData extends TanStackRowData>({
  row,
  scrollState,
}: {
  row: TanStackRow<TRowData>
  scrollState: DataTableScrollState
}) {
  const { appliedPlugins } = useDataTableCtx<TRowData>()
  const isSelected = row.getIsSelected()

  function getMergedCellProps(cell: Cell<TRowData, unknown>) {
    return appliedPlugins
      .map((plugin) => plugin.getCellProps?.(cell, scrollState) || {})
      .reduce((acc, props) => {
        // Merge className and style, shallow merge for others
        if (props.className) {
          acc.className = acc.className ? `${acc.className} ${props.className}` : props.className
        }
        if (props.style) {
          acc.style = { ...acc.style, ...props.style }
        }
        // biome-ignore lint/performance/noAccumulatingSpread: idk
        return { ...acc, ...props }
      }, {})
  }

  return (
    <DataTableRowCtx.Provider value={row}>
      <TableRow data-state={isSelected && "selected"}>
        {row.getLeftVisibleCells().map((cell) => {
          const isSelectColumn = cell.column.id === "__select__"
          const cellProps = getMergedCellProps(cell)
          return (
            <TableCell
              {...cellProps}
              className={clsx(
                "sticky left-0 z-10",
                {
                  "!bg-neutral-0 !p-0 !shadow-[inset_0px_-1px_0px_0px_var(--wb-color-neutral-0)]":
                    isSelectColumn,
                },
                cellProps.className
              )}
              style={{
                minWidth: cell.column.getSize(),
                left: cell.column.getStart(),
                ...(cellProps.style || {}),
              }}
              key={cell.id}
            >
              <div
                className={clsx({
                  "opacity-0 group-hover:opacity-100": isSelectColumn,
                  "opacity-100": isSelectColumn && isSelected,
                })}
              >
                <DataTablePinnedShadow isHorizontalScroll={scrollState.isHorizontalScroll}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </DataTablePinnedShadow>
              </div>
            </TableCell>
          )
        })}
        {row.getCenterVisibleCells().map((cell) => {
          const cellProps = getMergedCellProps(cell)
          return (
            <TableCell
              {...cellProps}
              data-state={isSelected && "selected"}
              key={cell.id}
              className={clsx(cellProps.className)}
              style={{
                minWidth: cell.column.getSize(),
                ...(cellProps.style || {}),
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          )
        })}
        {row.getRightVisibleCells().map((cell) => (
          <RightTableCell
            key={cell.id}
            cell={cell}
            scrollState={scrollState}
            isSelected={isSelected}
            getMergedCellProps={getMergedCellProps}
          />
        ))}
      </TableRow>

      {/* Expanded row content, works automatically with useRowExpansion plugin */}
      <DataTableRowExpanded row={row} />
    </DataTableRowCtx.Provider>
  )
}

function RightTableCell<TRowData extends TanStackRowData>({
  cell,
  scrollState,
  isSelected,
  getMergedCellProps,
}: {
  cell: Cell<TRowData, unknown>
  scrollState: DataTableScrollState
  isSelected: boolean
  getMergedCellProps: (cell: Cell<TRowData, unknown>) => any
}) {
  const cellProps = getMergedCellProps(cell)
  const { scrollLeft, scrollWidth } = scrollState
  const cellRef = useRef<HTMLTableCellElement>(null)
  const cellWidth = cellRef.current?.clientWidth ?? 0
  return (
    <TableCell
      {...cellProps}
      ref={cellRef}
      className={clsx(
        "sticky right-0 bg-[transparent] data-[horizontal-end]:bg-[--wb-table-row-background-default]",
        cellProps.className
      )}
      data-horizontal-end={scrollLeft >= scrollWidth - cellWidth ? "" : undefined}
      data-state={isSelected && "selected"}
      key={cell.id}
      style={{
        minWidth: cell.column.getSize(),
        ...(cellProps.style || {}),
      }}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell>
  )
}
